if (!customElements.get('video-loader')) {
  customElements.define('video-loader', class VideoLoader extends HTMLElement {
    constructor() {
      super();
      this.preloadVideo = this.getAttribute('data-preload') === 'true';
      this.handleResize = this.handleResize.bind(this);

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this), {
        root: null, // Use the viewport as the root
        threshold: 0.5, // Trigger when 50% of the element is visible
      });
    }

    connectedCallback() {
      if (this.preloadVideo) {
        this.querySelectorAll('video').forEach((video) => this.loadSources(video));
      }

      this.observeVideos();

      this.syncPlayTriggerFromActiveVideo();
      window.addEventListener('resize', this.handleResize);

      this.querySelector('mute-trigger')?.addEventListener('click', (event) => {
        this.querySelector('.icon-mute').classList.toggle('hidden');
        this.querySelector('.icon-speaker').classList.toggle('hidden');
        const videos = this.querySelectorAll('video');
        videos.forEach((video) => {
          video.muted = !video.muted;
        });
      });

      this.querySelector('play-trigger')?.addEventListener('click', () => {
        const videos = this.querySelectorAll('video');
        const activeVideo = this.getActiveVideo();
        const shouldPlay = activeVideo ? activeVideo.paused : true;

        videos.forEach((video) => {
          if (shouldPlay) {
            delete video.dataset.userPaused;
            delete video.dataset.scrollPaused;
            this.loadSources(video);
            video.play().catch(() => { });
          } else {
            // Remember this was deliberate, so scrolling back to the video doesn't restart it.
            video.dataset.userPaused = 'true';
            delete video.dataset.scrollPaused;
            video.pause();
          }
        });

        this.setPlayTriggerState(shouldPlay);
      });
    }

    getActiveVideo() {
      const isMobileViewport = window.matchMedia('(max-width: 989px)').matches;
      const mobileVideo = this.querySelector('[data-mobile-video] video');
      const desktopVideo = this.querySelector('[data-desktop-video] video');

      if (isMobileViewport && mobileVideo) {
        return mobileVideo;
      }

      if (!isMobileViewport && desktopVideo) {
        return desktopVideo;
      }

      return mobileVideo || desktopVideo || this.querySelector('video');
    }

    setPlayTriggerState(isPlaying) {
      const playTrigger = this.querySelector('play-trigger');
      if (!playTrigger) {
        return;
      }

      playTrigger.setAttribute('aria-label', isPlaying ? 'Playing' : 'Paused');
    }

    syncPlayTriggerFromActiveVideo() {
      const activeVideo = this.getActiveVideo();
      if (activeVideo) {
        this.setMediaPlayingState(activeVideo, !activeVideo.paused);
      }
      this.setPlayTriggerState(activeVideo ? !activeVideo.paused : false);
    }

    setMediaPlayingState(video, isPlaying) {
      const mediaContainer = video.closest('.video-section__media');
      if (!mediaContainer) {
        return;
      }

      mediaContainer.classList.toggle('media--playing', isPlaying);

      // Sticks once playback has begun: the poster covers the initial load, but shouldn't come
      // back every time the video pauses on scroll.
      if (isPlaying) {
        mediaContainer.classList.add('media--started');
      }
    }

    handleResize() {
      this.syncPlayTriggerFromActiveVideo();
    }

    // Define a function to observe videos within the custom element and load them when they become visible
    observeVideos() {
      const videos = this.querySelectorAll('video');

      videos.forEach((video) => {
        this.intersectionObserver.observe(video);

        video.setAttribute('data-paused', 'false');

        video.addEventListener('play', () => {
          video.setAttribute('data-paused', 'false');
          this.setMediaPlayingState(video, true);
          if (video === this.getActiveVideo()) {
            this.setPlayTriggerState(true);
          }
        });

        video.addEventListener('pause', () => {
          video.setAttribute('data-paused', 'true');
          this.setMediaPlayingState(video, false);
          if (video === this.getActiveVideo()) {
            this.setPlayTriggerState(false);
          }
        });

      });
    }

    // Attach the sources and load the video exactly once. video.load() resets playback to the
    // start, so calling it again on a later scroll-in is what used to restart the video.
    loadSources(video) {
      if (video.dataset.sourcesLoaded === 'true') {
        return;
      }
      video.dataset.sourcesLoaded = 'true';

      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.getAttribute('data-src');
      });
      video.load();

      if (video.getAttribute('autoplay') === 'autoplay') {
        video.muted = true;
        video.addEventListener('loadeddata', () => {
          if (video.dataset.userPaused !== 'true') {
            video.play().catch(() => { });
          }
        }, { once: true });
      }
    }

    // Handle the intersection changes (when videos become visible/invisible)
    handleIntersection(entries) {
      entries.forEach((entry) => {
        const video = entry.target;

        if (!entry.isIntersecting) {
          // Pause on the way out, but flag that we were the ones who did it so it can be
          // resumed — rather than restarted — when it scrolls back into view.
          if (!video.paused) {
            video.dataset.scrollPaused = 'true';
            video.pause();
          }
          return;
        }

        this.loadSources(video);

        // A video the visitor paused on purpose stays paused.
        if (video.dataset.userPaused === 'true') {
          return;
        }

        if (video.dataset.scrollPaused === 'true') {
          delete video.dataset.scrollPaused;
          video.play().catch(() => { });
        } else if (video.getAttribute('autoplay') === 'autoplay' && video.currentTime === 0) {
          video.muted = true;
          video.play().catch(() => { });
        }
      });
    }

    // Clean up the Intersection Observer when the element is removed from the DOM
    disconnectedCallback() {
      this.intersectionObserver.disconnect();
      window.removeEventListener('resize', this.handleResize);
    }
  });
}
